// src/pages/GeneratedTestView.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FiChevronLeft,
  FiEdit2,
  FiFileText,
  FiSettings,
  FiTrash2,
  FiCheckCircle,
  FiPlus,
  FiX,
  FiSave,
} from "react-icons/fi";
import {
  useUpdateTestMetaMutation,
  useSetCorrectOptionMutation,
  useUpdateQuestionMutation,
  useUpdateOptionMutation,
  useDeleteOptionMutation,
  useAddOptionMutation,
  useDeleteQuestionMutation,
  useAddQuestionMutation,
  useGetMyTestByIdQuery
} from "../context/apiSlice";
import PdfSettingsModal from "../components/PdfSettingsModal";

const ABCD = ["A", "B", "C", "D", "E"];


export default function GeneratedTestView({ test, onBack }) {
  const [updateQuestion] = useUpdateQuestionMutation();
  const [updateOption] = useUpdateOptionMutation();

  const [pdfOpen, setPdfOpen] = useState(false);
  const [deleteOptionApi, { isLoading: isDeletingOption }] = useDeleteOptionMutation();
  const [addOptionApi, { isLoading: isAddingOption }] = useAddOptionMutation();

  const [deleteQuestionApi, { isLoading: isDeletingQuestion }] = useDeleteQuestionMutation();
  const [addQuestionApi, { isLoading: isAddingQuestion }] = useAddQuestionMutation();

  const [showAnswers, setShowAnswers] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const baseFromProp = test?.test ? test.test : test;

  const testId =
    baseFromProp?.id ||
    test?.id ||
    test?.test?.id ||
    test?.testId;

  const { data, refetch, isFetching } = useGetMyTestByIdQuery(testId, {
    skip: !testId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const base = data?.test || baseFromProp;

  const [updateTestMeta, { isLoading: isSavingMeta }] = useUpdateTestMetaMutation();
  const [setCorrectOption, { isLoading: isSettingCorrect }] = useSetCorrectOptionMutation();

  const [draft, setDraft] = useState(() => normalizeTest(base));

  useEffect(() => {
    setDraft(normalizeTest(base));
  }, [base]);

  const totalQuestions = useMemo(() => draft?.questions?.length ?? 0, [draft]);


  async function handleAddQuestion() {
    if (!testId) return alert("Test ID topilmadi");

    try {
      // yangi savol: bo‘sh matn bilan ham bo‘ladi, lekin backend text majburiy
      const qText = "Yangi savol";

      await addQuestionApi({
        testId,
        text: qText,
        explanation: null,
      }).unwrap();

      await refetch();        // ✅ DB dan qayta olib keladi
      setShowAnswers(true);
      setEditMode(true);
    } catch (e) {
      console.error(e);
      alert(e?.data?.message || e?.error || "Savol qo‘shishda xato");
    }
  }


  function getAnswerIndex(q) {
    if (Number.isInteger(q.correctIndex)) return q.correctIndex;

    if (q.correctOptionId && Array.isArray(q.options)) {
      const idx = q.options.findIndex(
        (o) => (o?.id || o) === q.correctOptionId
      );
      return idx >= 0 ? idx : -1;
    }

    if (Number.isInteger(q.answerIndex)) return q.answerIndex;

    if (typeof q.answer === "string") {
      const idx = q.answer.toUpperCase().charCodeAt(0) - 65;
      return idx >= 0 ? idx : -1;
    }
    return -1;
  }

  function setTitle(value) {
    setDraft((prev) => ({ ...prev, title: value }));
  }

  function setSummary(value) {
    setDraft((prev) => ({ ...prev, summary: value }));
  }

  function setQuestionText(qIndex, value) {
    setDraft((prev) => {
      const next = structuredClone(prev);
      next.questions[qIndex].text = value;
      return next;
    });
  }

  function setOptionText(qIndex, optIndex, value) {
    setDraft((prev) => {
      const next = structuredClone(prev);
      const opt = next.questions[qIndex].options?.[optIndex];

      if (opt && typeof opt === "object") {
        next.questions[qIndex].options[optIndex] = { ...opt, text: value };
      } else {
        next.questions[qIndex].options[optIndex] = {
          id: `${next.questions[qIndex].id || "q"}_${optIndex}`,
          text: value,
        };
      }
      return next;
    });
  }

  function addQuestion() {
    setDraft((prev) => {
      const next = structuredClone(prev);
      next.questions.push({
        id: cryptoRandomId(),
        text: "",
        explanation: null,
        options: [
          { id: cryptoRandomId(), text: "" },
          { id: cryptoRandomId(), text: "" },
          { id: cryptoRandomId(), text: "" },
          { id: cryptoRandomId(), text: "" },
        ],
        correctOptionId: null,
        correctIndex: 0,
      });
      return next;
    });
  }

  function deleteQuestion(qIndex) {
    setDraft((prev) => {
      const next = structuredClone(prev);
      next.questions.splice(qIndex, 1);
      return next;
    });
  }

  function addOption(qIndex) {
    setDraft((prev) => {
      const next = structuredClone(prev);
      const q = next.questions[qIndex];
      q.options = Array.isArray(q.options) ? q.options : [];
      if (q.options.length >= 4) return prev;
      q.options.push({ id: cryptoRandomId(), text: "" });
      return next;
    });
  }

  function deleteOption(qIndex, optIndex) {
    setDraft((prev) => {
      const next = structuredClone(prev);
      const q = next.questions[qIndex];
      q.options = Array.isArray(q.options) ? q.options : [];
      if (q.options.length <= 2) return prev;

      const deleted = q.options.splice(optIndex, 1)[0];

      const ans = getAnswerIndex(q);
      let newAns = ans;

      if (q.correctOptionId && deleted?.id && q.correctOptionId === deleted.id) {
        q.correctOptionId = q.options[0]?.id || null;
        newAns = 0;
      } else if (ans === optIndex) {
        newAns = 0;
      } else if (ans > optIndex) {
        newAns = ans - 1;
      }

      q.correctIndex = newAns;
      q.answerIndex = newAns;

      return next;
    });
  }

  function startEdit() {
    setEditMode(true);
    setShowAnswers(true);
  }

  function cancelEdit() {
    // ✅ test emas, base bilan reset qil
    setDraft(normalizeTest(base));
    setEditMode(false);
  }

  async function handleSetCorrect(qid, optionId) {
    if (!testId) return alert("Test ID topilmadi");
    if (!qid || !optionId) return;

    try {
      await setCorrectOption({ testId, qid, optionId }).unwrap();

      setDraft((prev) => {
        const next = structuredClone(prev);
        const q = next.questions.find((x) => x.id === qid);
        if (q) {
          q.correctOptionId = optionId;
          const idx = (q.options || []).findIndex((o) => o?.id === optionId);
          if (idx >= 0) {
            q.correctIndex = idx;
            q.answerIndex = idx;
          }
        }
        return next;
      });
    } catch (e) {
      console.error(e);
      alert(e?.data?.message || e?.error || "To‘g‘ri javobni belgilashda xato");
    }
  } async function handleAddOption(qid) {
    if (!testId) return alert("Test ID topilmadi");

    try {
      await addOptionApi({
        testId,
        qid,
        text: "", // bo‘sh variant
      }).unwrap();

      await refetch();
    } catch (e) {
      console.error(e);
      alert(e?.data?.message || e?.error || "Variant qo‘shishda xato");
    }
  }


  async function saveEdit() {
    if (!testId) return alert("Test ID topilmadi");

    try {
      await updateTestMeta({
        id: testId,
        title: draft.title,
        summary: draft.summary,
      }).unwrap();

      await refetch(); // xohlasang olib tashlasa ham bo'ladi (cache update ishlaydi)

      setEditMode(false);
    } catch (e) {
      console.error(e);
      alert(e?.data?.message || e?.error || "Saqlashda xato");
    }
  }

  // ✅ shu yerdan keyin sening UI return’ing davom etadi...


  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      {/* Yuqori panel */}
      <div className="flex items-center justify-between mb-4">
        <button
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          type="button"
          onClick={onBack}
        >
          <FiChevronLeft className="w-4 h-4" />
          Orqaga
        </button>

        <div className="text-xs text-gray-400">{totalQuestions} ta savol</div>
      </div>

      {/* Test nomi + tugmalar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        {!editMode ? (
          <h2 className="text-xl font-semibold text-gray-900">
            {draft?.title || "Nomsiz test"}
          </h2>
        ) : (
          <input
            value={draft.title || ""}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full md:w-[520px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400"
            placeholder="Test nomi..."
          />
        )}

        <div className="flex flex-wrap gap-2 text-xs">
          {!editMode && (
            <button
              type="button"
              onClick={() => setShowAnswers((p) => !p)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              {showAnswers ? "Javoblarni yashirish" : "Javoblarni ko‘rsatish"}
            </button>
          )}

          {!editMode ? (
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              <FiEdit2 className="w-4 h-4" />
              Testni tahrirlash
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={saveEdit}
                disabled={isSavingMeta}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-black text-white hover:bg-gray-900 disabled:opacity-60"
              >
                <FiSave className="w-4 h-4" />
                {isSavingMeta ? "Saqlanmoqda..." : "Saqlash"}
              </button>

              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                <FiX className="w-4 h-4" />
                Bekor qilish
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => setPdfOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            <FiFileText className="w-4 h-4" />
            PDF eksport
          </button>


          <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50">
            <FiSettings className="w-4 h-4" />
            Sozlamalar
          </button>

          <button className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-200 text-red-500 hover:bg-red-50">
            <FiTrash2 className="w-4 h-4" />
            O‘chirish
          </button>
        </div>
      </div>

      {/* Qisqacha mazmun */}
      <div className="mb-6 rounded-xl border border-gray-100 bg-gray-50 px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-800">Qisqacha mazmun</h3>

          {editMode && (
            <button
              type="button"
              onClick={handleAddQuestion}
              disabled={isAddingQuestion}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <FiPlus className="w-4 h-4" />
              {isAddingQuestion ? "Qo‘shilmoqda..." : "Savol qo‘shish"}
            </button>

          )}
        </div>

        {!editMode ? (
          <p className="mt-1 text-sm text-gray-600">
            {draft?.summary?.trim()
              ? draft.summary
              : `${draft?.title || "Test"} bo‘yicha ${totalQuestions} ta test.`}
          </p>
        ) : (
          <textarea
            value={draft.summary || ""}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400"
            placeholder="Qisqacha mazmun..."
          />
        )}
      </div>

      {/* Savollar */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Savollar</h3>

        <div className="space-y-4">
          {(draft?.questions || []).map((q, qIndex) => {
            const ans = getAnswerIndex(q);
            const opts = Array.isArray(q.options) ? q.options : [];

            return (
              <div key={q.id || qIndex} className="border border-gray-100 rounded-xl">
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-start gap-3 justify-between">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-6 w-6 rounded-full border border-gray-300 flex items-center justify-center text-xs">
                      {qIndex + 1}
                    </div>

                    {!editMode ? (
                      <p className="text-sm text-gray-800">{q.text}</p>
                    ) : (
                      <div className="w-[min(720px,70vw)]">
                        <label className="block text-[11px] text-gray-500 mb-1">
                          Savol matni
                        </label>
                        <input
                          value={q.text || ""}
                          onChange={(e) => setQuestionText(qIndex, e.target.value)}
                          onBlur={async () => {
                            try {
                              await updateQuestion({
                                testId,
                                qid: q.id,
                                text: q.text,
                              }).unwrap();
                            } catch (e) {
                              alert(e?.data?.message || "Savol saqlanmadi");
                            }
                          }}
                        />

                      </div>
                    )}
                  </div>

                  {editMode && (
                    <button
                      type="button"
                      onClick={() => deleteQuestion(qIndex)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"
                      title="Savolni o‘chirish"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Options */}
                <div className="px-4 py-3 space-y-2">
                  {opts.map((opt, optIndex) => {
                    const optId = typeof opt === "object" ? opt.id : undefined;
                    const optText = typeof opt === "object" ? opt.text : String(opt ?? "");

                    const isCorrect =
                      showAnswers &&
                      (q.correctOptionId
                        ? optId && q.correctOptionId === optId
                        : optIndex === ans);

                    return (
                      <div
                        key={optId || optIndex}
                        className={`group flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${isCorrect
                          ? "bg-green-50 border-green-200 text-green-800"
                          : "bg-white border-gray-200 text-gray-800"
                          }`}
                      >
                        <span className="text-xs font-semibold text-gray-500 w-5">
                          {ABCD[optIndex] || String.fromCharCode(65 + optIndex)}
                        </span>

                        {!editMode ? (
                          <span className="flex-1">{optText}</span>
                        ) : (
                          <input
                            value={optText}
                            onChange={(e) => setOptionText(qIndex, optIndex, e.target.value)}
                            onBlur={async () => {
                              try {
                                // oid = optId bo‘lsa optId yuboramiz, bo‘lmasa optIndex yuboramiz (fallback modelga)
                                const oid = optId ?? String(optIndex);

                                await updateOption({
                                  testId,
                                  qid: q.id,
                                  oid,
                                  text: optText,
                                }).unwrap();

                                // xohlasang refetch ham qil (lekin shart emas)
                                // await refetch();
                              } catch (e) {
                                alert(e?.data?.message || "Variant saqlanmadi");
                              }
                            }}
                            className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-gray-400"
                          />
                        )}


                        {/* Correct picker (edit mode) -> BACKENDGA yuboradi */}
                        {editMode && (
                          <button
                            type="button"
                            disabled={!optId || isSettingCorrect}
                            onClick={() => handleSetCorrect(q.id, optId)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs ${isCorrect
                              ? "border-green-200 bg-green-50 text-green-700"
                              : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                              } ${(!optId || isSettingCorrect) ? "opacity-60" : ""}`}
                            title={
                              !optId
                                ? "Option ID yo‘q (backenddan option object kelishi kerak)"
                                : "To‘g‘ri javob qilib belgilash"
                            }
                          >
                            <FiCheckCircle className="w-4 h-4" />
                            To‘g‘ri
                          </button>
                        )}

                        {!editMode && isCorrect && (
                          <FiCheckCircle className="w-4 h-4 text-green-500" />
                        )}

                        {editMode && (
                          <button
                            type="button"
                            onClick={() => deleteOption(qIndex, optIndex)}
                            className="opacity-0 group-hover:opacity-100 transition inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"
                            title="Variantni o‘chirish"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {/* Add option (edit mode) */}
                  {editMode && opts.length < 5 && (
                    <button
                      type="button"
                      disabled={isAddingOption}
                      onClick={() => handleAddOption(q.id)}
                      className="mt-2 inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      <FiPlus className="w-4 h-4" />
                      {isAddingOption ? "Qo‘shilmoqda..." : "Variant qo‘shish"}
                    </button>
                  )}

                </div>
              </div>
            );
          })}

          {(draft?.questions || []).length === 0 && (
            <div className="text-sm text-gray-400">Savollar topilmadi.</div>
          )}
        </div>
      </div>
      
<PdfSettingsModal
  open={pdfOpen}
  onClose={() => setPdfOpen(false)}
  testId={testId}
/>
    </div>
  );
}

/** test kelgan formatlarni bir xil qilib normalize qilib olamiz */
function normalizeTest(test) {
  const base = test?.test ? test.test : test;
  const t = base || {};

  const questions = (t.questions || []).map((q, idx) => {
    const qid = q.id || String(idx + 1);

    // options: backenddan [{id,text}] keladi.
    // fallback: agar string[] kelib qolsa, objectga aylantiramiz.
    const options = Array.isArray(q.options)
      ? q.options.map((o, oi) => {
        if (o && typeof o === "object") {
          return { id: o.id || `${qid}_${oi}`, text: o.text ?? "" };
        }
        return { id: `${qid}_${oi}`, text: String(o ?? "") };
      })
      : [];

    // correct
    const correctOptionId =
      q.correctOptionId || q.correct_option_id || null;

    // index fallback
    let correctIndex = -1;
    if (correctOptionId) {
      const i = options.findIndex((o) => o.id === correctOptionId);
      correctIndex = i >= 0 ? i : -1;
    } else if (Number.isInteger(q.correctIndex)) correctIndex = q.correctIndex;
    else if (Number.isInteger(q.answerIndex)) correctIndex = q.answerIndex;

    return {
      id: qid,
      text: q.text ?? q.question ?? q.questionText ?? "",
      explanation: q.explanation ?? null,
      options,
      correctOptionId: correctOptionId || (correctIndex >= 0 ? options[correctIndex]?.id : null),
      correctIndex,
      answerIndex: q.answerIndex,
    };
  });

  return {
    ...t,
    id: t.id,
    title: t.title || "",
    summary: t.summary || "",
    questions,
  };
}

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id_" + Math.random().toString(16).slice(2);
}

